import ViiluFullPageDialog from 'ui/components/ViiluFullPageDialog'
import { useState } from 'react'
import { useIntl } from 'react-intl'
import { Tab, Tabs } from 'react-md'
import useInvoice from '../useInvoice'
import { FormProvider, useForm } from 'react-hook-form'
import { useZodValidationResolver } from 'util/hooks/useZodValidationResolver'
import { InvoiceFormSchema } from '../validation'
import type { InvoiceFormInputs } from '../validation'
import Attachments from './Attachments'
import ProductTable from './ProductTable'
import InvoiceStatus from './InvoiceStatus'
import AdditionalInformation from './AdditionalInformation'
import styled from 'styled-components'
import { customerHeaders, metadataHeaders, amountHeaders } from './constants'

const Table = styled.table`
	border-collapse: collapse;
	border: 1px solid rgba(0, 0, 0, 0.2);
	min-width: 30vw;

	td {
		padding: 4px 8px;
		white-space: nowrap;
		border-bottom: 1px solid rgba(0, 0, 0, 0.2);
	}

	td:nth-child(2) {
		text-align: right;
	}
`

const TableContainer = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 1rem;
`

type Props = {
	id?: string | null
	refresh: () => void
	onHide: () => void
}

const InvoiceView = ({
	id,
	//refresh,
	onHide,
}: Props) => {
	const intl = useIntl()

	const { invoice } = useInvoice(id)
	const [tabIndex, setTabIndex] = useState(0)

	const resolver =
		useZodValidationResolver<InvoiceFormInputs>(InvoiceFormSchema)

	const formMethods = useForm<InvoiceFormInputs>({
		resolver,
		defaultValues: {},
		mode: 'onBlur',
		shouldUnregister: false,
	})

	const getValue = (key: any) => {
		if (!invoice) return '-'

		return invoice[key] || '-'
	}

	//TODO:
	const renderInvoiceDetails = () => {
		return (
			<TableContainer>
				<Table>
					<tbody>
						{customerHeaders.map((header) => {
							const { key, title } = header
							return (
								<tr key={String(key)}>
									<td>{title(intl)}</td>
									<td>{getValue(key) || '-'}</td>
								</tr>
							)
						})}
					</tbody>
				</Table>
				<Table>
					<tbody>
						{metadataHeaders.map((header) => {
							const { key, title } = header
							return (
								<tr key={String(key)}>
									<td>{title(intl)}</td>
									<td>{getValue(key) || '-'}</td>
								</tr>
							)
						})}
					</tbody>
				</Table>
				<Table>
					<tbody>
						{amountHeaders.map((header) => {
							const { key, title } = header
							return (
								<tr key={String(key)}>
									<td>{title(intl)}</td>
									<td>{getValue(key) || '-'}</td>
								</tr>
							)
						})}
					</tbody>
				</Table>
			</TableContainer>
		)
	}

	const renderTabs = () => {
		return (
			<Tabs
				tabId="users-mobile-tab"
				mobile
				style={{
					flexShrink: 0,
					borderBottomStyle: 'solid',
					borderBottomColor: 'rgba(0,0,0,0.2)',
					borderBottomWidth: 1,
					marginBottom: '1rem',
				}}
				alignToKeyline={false}
				defaultTabIndex={tabIndex}
				activeTabIndex={tabIndex}
				onTabChange={() => {}}
				indicatorHeight={3}
			>
				<Tab
					key={0}
					style={
						tabIndex === 0
							? {
									color: 'var(--color-primary-dark)',
									fontWeight: '700',
									minWidth: '20%',
							  }
							: {
									color: 'var(--color-text-subtle)',
									minWidth: '20%',
							  }
					}
					label={intl.formatMessage({
						defaultMessage: '1. Laskutus',
						description: '1. Invoicing',
					})}
					onClick={() => setTabIndex(0)}
				/>
				<Tab
					key={1}
					style={
						tabIndex === 1
							? {
									color: 'var(--color-primary-dark)',
									fontWeight: '700',
									minWidth: '20%',
							  }
							: {
									color: 'var(--color-text-subtle)',
									minWidth: '20%',
							  }
					}
					label={intl.formatMessage({
						defaultMessage: '2. Tuotteet',
						description: '2. Products',
					})}
					onClick={() => setTabIndex(1)}
				/>
				<Tab
					key={2}
					style={
						tabIndex === 2
							? {
									color: 'var(--color-primary-dark)',
									fontWeight: '700',
									minWidth: '20%',
							  }
							: {
									color: 'var(--color-text-subtle)',
									minWidth: '20%',
							  }
					}
					label={intl.formatMessage({
						defaultMessage: '3. Laskutilanne',
						description: '3. Invoice Status',
					})}
					onClick={() => setTabIndex(2)}
				/>
				<Tab
					key={3}
					style={
						tabIndex === 3
							? {
									color: 'var(--color-primary-dark)',
									fontWeight: '700',
									minWidth: '20%',
							  }
							: {
									color: 'var(--color-text-subtle)',
									minWidth: '20%',
							  }
					}
					label={intl.formatMessage({
						defaultMessage: '4. Lisätietoja',
						description: '4. Additional Information',
					})}
					onClick={() => setTabIndex(3)}
				/>
				<Tab
					key={4}
					style={
						tabIndex === 4
							? {
									color: 'var(--color-primary-dark)',
									fontWeight: '700',
									minWidth: '20%',
							  }
							: {
									color: 'var(--color-text-subtle)',
									minWidth: '20%',
							  }
					}
					label={intl.formatMessage({
						defaultMessage: '5. Liitteet',
						description: '5. Attachments',
					})}
					onClick={() => setTabIndex(4)}
				/>
			</Tabs>
		)
	}

	const renderChildren = () => {
		return (
			<div
				className="margin-left--lg margin-right--lg"
				style={{ paddingBottom: 64 }}
			>
				{renderTabs()}
				<FormProvider {...formMethods}>
					{tabIndex === 0 && renderInvoiceDetails()}
					{tabIndex === 1 && <ProductTable invoice={invoice} />}
					{tabIndex === 2 && <InvoiceStatus invoice={invoice} />}
					{tabIndex === 3 && <AdditionalInformation invoice={invoice} />}
					{tabIndex === 4 && <Attachments invoice={invoice} />}
				</FormProvider>
			</div>
		)
	}

	const actions = [
		{
			id: 'dialog-cloes',
			children: intl.formatMessage({
				defaultMessage: 'Sulje',
				description: 'Close button',
			}),
			onClick: onHide,
		},
	]

	return (
		<ViiluFullPageDialog
			id="invoice-view-dialog"
			visible
			title={intl.formatMessage({
				defaultMessage: 'Lasku',
				description: 'Title of invoice',
			})}
			toolbarStyle={{
				background: 'var(--color-primary-dark)',
				boxShadow: 'none',
			}}
			actions={actions}
			onHide={onHide}
		>
			{renderChildren()}
		</ViiluFullPageDialog>
	)
}

export default InvoiceView
